import React from 'react';

export const tonelabContent = {

  pageMaxWidth: '1200px',

  breadcrumb: {
    text: 'Back to Home',
    url: '/'
  },

  sections: [

    // HERO
    {
      id: 'hero',
      spacing: 'clamp(32px, 5vw, 64px)',
      blocks: [
        {
          type: 'container',
          narrow: true,
          blocks: [
            {
              type: 'custom',
              render: () => (
                <span style={{
                  background: 'var(--bg-accent)',
                  color: 'var(--text-accent)',
                  padding: '8px 20px',
                  borderRadius: '25px',
                  fontSize: '13px',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  display: 'inline-block'
                }}>
                  Research & Tools
                </span>
              )
            }
          ]
        },

        { type: 'spacer', size: 'small' },

        {
          type: 'title',
          text: 'Tonelab',
          size: 'h1'
        },

        {
          type: 'text',
          content: 'A brand tone and voice system — a growing archive that organizes how brands communicate through their language, messaging, and identity.',
          size: 'large',
          align: 'left'
        },

        { type: 'spacer', size: 'small' },

        {
          type: 'text',
          content: '3,000+ brands across 20+ sectors. Each brand entry includes tagline, tone words, messaging approach, brand colors, logo, downloadable brand assets, guidelines, and additional resources.'
        },

        { type: 'spacer', size: 'small' },

        {
          type: 'custom',
          render: () => (
            <div style={{
              display: 'flex',
              gap: '16px',
              flexWrap: 'wrap'
            }}>
              <a
                href="https://tonelab.so"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  background: 'linear-gradient(135deg, rgb(61, 39, 41) 0%, rgb(39, 11, 14) 100%)',
                  color: 'white',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontWeight: '600'
                }}
              >
                Visit Tonelab Live
              </a>
              <span style={{
                background: 'linear-gradient(135deg, rgb(61, 39, 41) 0%, rgb(39, 11, 14) 100%)',
                color: 'white',
                padding: '12px 24px',
                borderRadius: '8px',
                fontWeight: '600'
              }}>
                Live & Ongoing
              </span>
            </div>
          )
        }
      ]
    },

    // LIVE EMBED — FIRST
    {
      id: 'live-demo',
      title: 'Tonelab Live',
      blocks: [
        {
          type: 'text',
          content: 'The full experience — search brands, filter by sector, switch between views, and explore individual brand breakdowns.',
          size: 'large'
        },

        { type: 'spacer', size: 'small' },

        {
          type: 'custom',
          render: () => (
            <div style={{
              position: 'relative',
              width: '100%',
              height: '80vh',
              minHeight: '600px',
              background: '#000',
              borderRadius: '20px',
              overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              border: '2px solid var(--border-color, rgba(255, 255, 255, 0.1))'
            }}>
              <iframe
                src="https://tonelab.so"
                title="Tonelab Live"
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none'
                }}
              />
            </div>
          )
        }
      ]
    },

    // WHAT'S INSIDE EACH BRAND
    {
      id: 'card-details',
      title: 'What\'s Inside Each Brand',
      blocks: [
        {
          type: 'container',
          background: true,
          blocks: [
            {
              type: 'custom',
              render: () => (
                <ul style={{
                  color: 'var(--text-secondary)',
                  fontSize: 'clamp(1rem, 1.8vw, 1.125rem)',
                  lineHeight: '1.8',
                  paddingLeft: '24px',
                  margin: 0
                }}>
                  <li>Industry & sector classification</li>
                  <li>Tagline & tone words</li>
                  <li>Brand concept & core tension</li>
                  <li>Brand mission & messaging approach</li>
                  <li>Tagline word count & language style</li>
                  <li>Statement, question, or command format</li>
                  <li>Logo & brand colors</li>
                  <li>Downloadable brand assets</li>
                  <li>Brand guidelines (if publicly available)</li>
                  <li>Additional resources — marketing, strategies, identity, articles</li>
                </ul>
              )
            }
          ]
        }
      ]
    },

    // WHERE IT STARTED — Notion screenshots as origin
    {
      id: 'origin',
      title: 'Where It Started',
      blocks: [
        {
          type: 'text',
          content: 'Tonelab started as an idea I needed to get out of my head. I began collecting brand taglines and tone notes in Notion — organizing them into cards and tables to see patterns across industries. That early version became the foundation for everything the app is today.',
          size: 'normal'
        },

        { type: 'spacer', size: 'small' },

        {
          type: 'custom',
          render: () => (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '12px',
              width: '100%'
            }}>
              <div style={{ position: 'relative' }}>
                <img src="/tonelab-screens/dark-cards.png" alt="" style={{ width: '100%', display: 'block', borderRadius: '8px', opacity: 0.85 }} />
                <span style={{
                  position: 'absolute',
                  bottom: '12px',
                  left: '12px',
                  background: 'rgba(0,0,0,0.7)',
                  color: '#fff',
                  padding: '4px 10px',
                  borderRadius: '4px',
                  fontSize: '11px',
                  fontWeight: '600',
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase'
                }}>Early Version — Notion</span>
              </div>
              <div style={{ position: 'relative' }}>
                <img src="/tonelab-screens/light-cards.png" alt="" style={{ width: '100%', display: 'block', borderRadius: '8px', opacity: 0.85 }} />
                <span style={{
                  position: 'absolute',
                  bottom: '12px',
                  left: '12px',
                  background: 'rgba(0,0,0,0.7)',
                  color: '#fff',
                  padding: '4px 10px',
                  borderRadius: '4px',
                  fontSize: '11px',
                  fontWeight: '600',
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase'
                }}>Early Version — Notion</span>
              </div>
            </div>
          )
        },

        { type: 'spacer', size: 'small' },

        {
          type: 'custom',
          render: () => (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '12px',
              width: '100%'
            }}>
              <div style={{ position: 'relative' }}>
                <img src="/tonelab-screens/dark-table.png" alt="" style={{ width: '100%', display: 'block', borderRadius: '8px', opacity: 0.85 }} />
                <span style={{
                  position: 'absolute',
                  bottom: '12px',
                  left: '12px',
                  background: 'rgba(0,0,0,0.7)',
                  color: '#fff',
                  padding: '4px 10px',
                  borderRadius: '4px',
                  fontSize: '11px',
                  fontWeight: '600',
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase'
                }}>Early Version — Notion Table</span>
              </div>
              <div style={{ position: 'relative' }}>
                <img src="/tonelab-screens/light-table.png" alt="" style={{ width: '100%', display: 'block', borderRadius: '8px', opacity: 0.85 }} />
                <span style={{
                  position: 'absolute',
                  bottom: '12px',
                  left: '12px',
                  background: 'rgba(0,0,0,0.7)',
                  color: '#fff',
                  padding: '4px 10px',
                  borderRadius: '4px',
                  fontSize: '11px',
                  fontWeight: '600',
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase'
                }}>Early Version — Notion Table</span>
              </div>
            </div>
          )
        },

        { type: 'spacer', size: 'small' },

        {
          type: 'text',
          content: 'From there I rebuilt everything from scratch as a full web application — with proper search, filtering, multiple view modes, individual brand detail pages, and downloadable assets.',
          size: 'normal'
        }
      ]
    },

    // HOW IT STARTED story
    {
      id: 'overview',
      title: 'The Story',
      blocks: [
        {
          type: 'container',
          background: true,
          blocks: [
            {
              type: 'text',
              content: 'I was looking into taglines for a friend\'s business, just collecting examples from different brands and seeing how they write and communicate. Every time I found a tagline I liked, I\'d try writing a variation that matched the tone he was going for.'
            },
            { type: 'spacer', size: 'small' },
            {
              type: 'text',
              content: 'I started organizing them by industry. Healthcare brands tend to write more directly than tech brands. Fintech brands use more reassuring language than traditional banks. Every time I found another brand, I\'d note these distinctions.'
            },
            { type: 'spacer', size: 'small' },
            {
              type: 'text',
              content: 'What started as 20 brands became 50, then 100, then 300 on Notion. Eventually I rebuilt the whole thing as a full web app — now it holds over 3,000 brands across 20+ sectors, with search, filters, multiple view modes, and detailed brand breakdowns.'
            }
          ]
        }
      ]
    },

    // FINDBRANDVOICE — BUILT ON TOP OF TONELAB
    {
      id: 'findbrandvoice',
      title: 'FindBrandVoice',
      blocks: [
        {
          type: 'text',
          content: 'Built on top of Tonelab\'s 3,000+ brand archive. FindBrandVoice takes all that brand data — taglines, tone words, messaging styles — and uses it to generate custom taglines for your brand through a guided wizard.',
          size: 'normal',
          align: 'left'
        },
        { type: 'spacer', size: 'small' },
        {
          type: 'text',
          content: 'You enter your brand name, pick your industry, select tone words, choose brands you admire as inspiration, and pick a tagline style. The app feeds all of that — including the real taglines from the brands you selected — into Claude to generate 5 tailored taglines narrowed down to your choices and style.',
          size: 'normal',
          align: 'left'
        },
        { type: 'spacer', size: 'small' },
        {
          type: 'custom',
          render: () => (
            <div style={{
              display: 'flex',
              gap: '12px',
              flexWrap: 'wrap'
            }}>
              <a
                href="https://findbrandvoice.com"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  background: 'linear-gradient(135deg, rgb(61, 39, 41) 0%, rgb(39, 11, 14) 100%)',
                  color: 'white',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontWeight: '600',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                Visit FindBrandVoice
              </a>
              <span style={{
                background: 'var(--bg-accent)',
                color: 'var(--text-accent)',
                padding: '12px 24px',
                borderRadius: '8px',
                fontWeight: '600'
              }}>
                Live
              </span>
            </div>
          )
        },
        { type: 'spacer', size: 'medium' },
        {
          type: 'custom',
          render: () => (
            <div style={{
              position: 'relative',
              width: '100%',
              height: '80vh',
              minHeight: '600px',
              background: '#000',
              borderRadius: '20px',
              overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              border: '2px solid var(--border-color, rgba(255, 255, 255, 0.1))'
            }}>
              <iframe
                src="https://findbrandvoice.com"
                title="FindBrandVoice Live"
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none'
                }}
              />
            </div>
          )
        },
        { type: 'spacer', size: 'medium' },
        {
          type: 'container',
          background: true,
          hidden: true, // tech stack panel — hidden, not deleted
          blocks: [
            {
              type: 'custom',
              render: () => (
                <div>
                  <h4 style={{
                    color: 'var(--text-accent)',
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    marginBottom: '12px'
                  }}>
                    FindBrandVoice Tech Stack
                  </h4>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '16px'
                  }}>
                    {[
                      { category: 'App', items: ['React Native', 'Expo SDK 54', 'TypeScript', 'Expo Router'] },
                      { category: 'Backend', items: ['Firebase Auth & Firestore', 'Claude (Anthropic API)', 'Vercel Serverless'] },
                      { category: 'Platform', items: ['Web', 'iOS', 'Android'] }
                    ].map((group, i) => (
                      <div key={i}>
                        <span style={{
                          color: 'var(--text-primary)',
                          fontSize: '0.85rem',
                          fontWeight: '600',
                          display: 'block',
                          marginBottom: '6px'
                        }}>
                          {group.category}
                        </span>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          {group.items.map((item, j) => (
                            <span key={j} style={{
                              color: 'var(--text-secondary)',
                              fontSize: '0.9rem'
                            }}>
                              {item}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )
            }
          ]
        }
      ]
    },

    // TECH STACK
    {
      id: 'stack',
      title: 'Tonelab — Built With',
      blocks: [
        {
          type: 'custom',
          render: () => {
            const stack = [
              { name: 'React 19', color: '#58C4DC', bg: 'rgba(88, 196, 220, 0.08)' },
              { name: 'Vite', color: '#BD34FE', bg: 'rgba(189, 52, 254, 0.08)' },
              { name: 'TailwindCSS', color: '#38BDF8', bg: 'rgba(56, 189, 248, 0.08)' },
              { name: 'Zustand', color: '#F6A623', bg: 'rgba(246, 166, 35, 0.08)' },
              { name: 'Framer Motion', color: '#FF0055', bg: 'rgba(255, 0, 85, 0.08)' },
              { name: 'Firebase', color: '#FFCA28', bg: 'rgba(255, 202, 40, 0.08)' },
              { name: 'Lucide Icons', color: '#F56565', bg: 'rgba(245, 101, 101, 0.08)' },
              { name: 'React Router', color: '#F44250', bg: 'rgba(244, 66, 80, 0.08)' },
              { name: 'Vercel', color: '#888', bg: 'rgba(136, 136, 136, 0.08)' },
            ];

            return (
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '10px'
              }}>
                {stack.map((tech) => (
                  <span
                    key={tech.name}
                    style={{
                      background: tech.bg,
                      border: `1px solid ${tech.color}22`,
                      color: tech.color,
                      padding: '10px 20px',
                      borderRadius: '10px',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      letterSpacing: '0.01em',
                      backdropFilter: 'blur(8px)',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {tech.name}
                  </span>
                ))}
              </div>
            );
          }
        }
      ]
    }

  ],

  callToAction: {
    brand: { bg: "#141315", fg: "#F8F9F9", btnBg: "#F8F9F9", btnFg: "#141315" , logo: "/brand-logos/tonelab.png", logoWidth: 72, logoRadius: 18 },
    title: 'Explore Tonelab',
    text: 'Browse 3,000+ brands and discover how they communicate',
    button: {
      text: 'Visit Tonelab',
      url: 'https://tonelab.so'
    }
  }
};
